// Поле «Заместитель» окна «Передать роль» (FR-018б; contracts/ui.md, «Действия и подтверждения»):
// кандидаты — GET /api/substitutions/candidates: активные пользователи без роли «сопровождение»
// (FR-018а), только ФИО, без почты.
// Список открыт администратору клиента и владельцу своей роли, которую можно передать. Поиск —
// по ФИО на каждое нажатие с короткой задержкой; себя сервер не предлагает, владельца роли
// не предлагает поле. Список не загрузился — не «Никого не нашлось», а ошибка с кодом в списке
// и выноска под полем с причиной, номером операции и «Повторить» (T135); на время повтора
// выноска на месте со спиннером (T168).
import { SearchPickerField } from '@kp/ui';
import type { SearchPickerOption } from '@kp/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { Ref } from 'react';
import { apiQuery } from '../../api/client.ts';
import { ListLoadError, failedListText, useQueryFailure } from '../load-error.tsx';

const FAILED = 'Не удалось загрузить список заместителей';

const SEARCH_DELAY_MS = 200;
const LIMIT = 20;

export interface DeputySearchFieldProps {
  value: SearchPickerOption | null;
  onChange: (option: SearchPickerOption) => void;
  /** Владелец роли: себе роль не передать. */
  excludeId?: string;
  error?: string;
  required?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function DeputySearchField({
  value,
  onChange,
  excludeId,
  error,
  required,
  ref,
}: DeputySearchFieldProps) {
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setQ(query.trim()), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [query]);
  const candidates = useQuery({
    ...apiQuery('substitutions.candidates', { query: { q: q || undefined, limit: LIMIT } }),
    placeholderData: keepPreviousData,
  });
  const { failure, retrying, retry } = useQueryFailure(candidates);
  // Пока сбой на месте (и на время его повтора), варианты прежнего поиска не показываются.
  const options = (failure ? [] : (candidates.data?.items ?? []))
    .filter((candidate) => candidate.id !== excludeId)
    .map<SearchPickerOption>((candidate) => ({ value: candidate.id, label: candidate.fullName }));
  return (
    <>
      <SearchPickerField
        ref={ref}
        label="Заместитель"
        placeholder="Выберите пользователя"
        searchPlaceholder="Найти по ФИО"
        emptyText={
          failure ? failedListText(FAILED, failure) : 'Никого не нашлось. Проверьте раскладку'
        }
        value={value}
        query={query}
        onQueryChange={setQuery}
        options={options}
        loading={candidates.isPending || (candidates.isFetching && options.length === 0)}
        onChange={onChange}
        error={error}
        required={required}
      />
      {failure ? (
        <ListLoadError what={FAILED} view={failure} retrying={retrying} onRetry={retry} />
      ) : null}
    </>
  );
}
